/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const BACKEND = process.env.REACT_APP_API_BASE;

async function apiCall(endpoint, method, body, token) {
  const res = await fetch(`${BACKEND}${endpoint}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined
  });
  return res.json();
}

async function apiUpload(endpoint, formData, token) {
  const res = await fetch(`${BACKEND}${endpoint}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData
  });
  return res.json();
}

const STEPS = [
  { n: 1, label: 'Screen' },
  { n: 2, label: 'Content' },
  { n: 3, label: 'Playlist' },
  { n: 4, label: 'Done' },
];

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(1);
  const [token, setToken] = useState('');
  const [screenName, setScreenName] = useState('');
  const [locName, setLocName] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  // Content step
  const [contentTab, setContentTab] = useState('image');
  const [contentName, setContentName] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [contentBody, setContentBody] = useState('');
  const [contentDuration, setContentDuration] = useState(10);
  const [contentFile, setContentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [contents, setContents] = useState([]); // added contents

  // Playlist step
  const [playlistName, setPlaylistName] = useState('');
  const [playlistId, setPlaylistId] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);

  // Created entities
  const [locationId, setLocationId] = useState(null);
  const [screenId, setScreenId] = useState(null);

  // Pre-fill token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) setToken(t);
  }, []);

  // Ödeme sonrası webhook lokasyon + ekranları oluşturmuş olabilir.
  // Bu durumda wizard'ı atla (hasScreens true yapıp dashboard'a dön).
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const t = await getSession();
        if (!t) return;
        for (let i = 0; i < 5 && !cancelled; i++) {
          const locs = await apiCall('/locations', 'GET', undefined, t);
          const total = (locs || []).reduce((a, l) => a + (l.screens || []).length, 0);
          if (total > 0) { onComplete(); return; }
          await new Promise(r => setTimeout(r, 1500));
        }
      } catch (e) { /* ignore */ }
    };
    check();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  // ── Step 1: Screen + Location ────────────────────────────────────────────────
  const handleStep1 = async () => {
    const errs = {};
    if (!token.trim()) errs.token = 'Token is required';
    if (!screenName.trim()) errs.screenName = 'Screen name is required';
    if (!locName.trim()) errs.locName = 'Location name is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setBusy(true);
    try {
      const t = await getSession();

      // Quota kontrolü: kota yoksa (yeni kullanıcı) → checkout'a git.
      // Ödeme sonrası webhook lokasyon + ekran + kotayı otomatik oluşturur.
      const quotaRes = await apiCall('/quota', 'GET', undefined, t);
      if (quotaRes && !quotaRes.isAdmin && quotaRes.quota === 0) {
        setErrors({ general: 'You need a plan to activate screens — starting checkout...' });
        const res = await fetch(`${BACKEND}/stripe/create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
          body: JSON.stringify({
            screen_count: 1,
            locName,
            locType: 'Other',
            screens: [{ token: token.trim(), name: screenName.trim() }],
            billing: 'yearly',
            trial_period_days: 14
          })
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else throw new Error(data.error || 'Checkout could not be started');
        return;
      }

      // Kota varsa → normal akış: lokasyon + ekran oluştur
      const loc = await apiCall('/locations', 'POST', { name: locName, type: 'Other' }, t);
      if (!loc.id) throw new Error('Could not create location');
      setLocationId(loc.id);

      // Activate screen
      const scr = await apiCall('/activate', 'POST', {
        device_token: token.trim(),
        name: screenName.trim(),
        location_id: loc.id
      }, t);
      if (!scr.ok) throw new Error(scr.error || 'Could not activate screen');
      setScreenId(scr.screen.id);

      setStep(2);
    } catch (e) {
      setErrors({ general: e.message });
    }
    setBusy(false);
  };

  // ── Step 2: Add content ───────────────────────────────────────────────────────
  const handleAddContent = async () => {
    if (!contentName.trim()) { setErrors({ contentName: 'Name is required' }); return; }
    setErrors({});
    setUploading(true);
    try {
      const t = await getSession();
      let newContent;

      if (contentTab === 'image' || contentTab === 'video' || contentTab === 'pdf') {
        if (!contentFile) { setErrors({ contentFile: 'Please choose a file' }); setUploading(false); return; }
        const fd = new FormData();
        fd.append('file', contentFile);
        fd.append('name', contentName);
        fd.append('type', contentTab);
        fd.append('duration_sec', contentDuration);
        newContent = await apiUpload('/contents/upload', fd, t);
      } else if (contentTab === 'url') {
        if (!contentUrl.trim()) { setErrors({ contentUrl: 'URL is required' }); setUploading(false); return; }
        newContent = await apiCall('/contents', 'POST', { name: contentName, type: 'url', url: contentUrl, duration_sec: contentDuration }, t);
      } else {
        if (!contentBody.trim()) { setErrors({ contentBody: 'Content text is required' }); setUploading(false); return; }
        newContent = await apiCall('/contents', 'POST', { name: contentName, type: 'data', body: contentBody, duration_sec: contentDuration }, t);
      }

      if (newContent.id) {
        setContents(prev => [...prev, newContent]);
        setContentName(''); setContentUrl(''); setContentBody(''); setContentFile(null); setContentDuration(10);
      }
    } catch (e) {
      setErrors({ general: e.message });
    }
    setUploading(false);
  };

  const handleRemoveContent = (id) => setContents(prev => prev.filter(c => c.id !== id));

  // ── Step 3: Playlist ──────────────────────────────────────────────────────────
  const handleCreatePlaylist = async () => {
    if (!playlistName.trim()) { setErrors({ playlistName: 'Playlist name is required' }); return; }
    if (contents.length === 0) { setErrors({ playlistName: 'Add at least one content item first' }); return; }
    setErrors({});
    setBusy(true);
    try {
      const t = await getSession();

      // Create playlist
      const pl = await apiCall('/playlists', 'POST', { name: playlistName, description: '' }, t);
      if (!pl.id) throw new Error('Could not create playlist');
      setPlaylistId(pl.id);

      // Add all contents to playlist
      for (const c of contents) {
        await apiCall(`/playlists/${pl.id}/items`, 'POST', { content_id: c.id }, t);
      }
      setPlaylistItems(contents);

      // Assign playlist to screen
      await apiCall(`/screens/${screenId}/playlist`, 'PATCH', { playlist_id: pl.id }, t);

      setStep(4);
    } catch (e) {
      setErrors({ general: e.message });
    }
    setBusy(false);
  };

  const typeLabel = { image: '🖼️ Image', video: '🎬 Video', pdf: '📄 PDF', url: '🌐 URL', data: '📋 Custom' };
  const acceptMap = { image: 'image/*', video: 'video/*', pdf: 'application/pdf' };

  return (
    <div style={{ minHeight: '100vh', background: '#F5EFE6', fontFamily: 'Helvetica Neue, Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#111', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Signage<span style={{ color: '#C45C6A' }}>Panel</span></div>
        <div style={{ fontSize: 12, color: '#666' }}>Setup wizard</div>
      </div>

      {/* Progress */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E0D8CF', padding: '12px 20px' }}>
        <div style={{ display: 'flex', gap: 0, maxWidth: 500, margin: '0 auto' }}>
          {STEPS.map((s, i) => (
            <div key={s.n} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {i < STEPS.length - 1 && (
                <div style={{ position: 'absolute', top: 14, left: '50%', width: '100%', height: 2, background: step > s.n ? '#8B1E2D' : '#E0D8CF', zIndex: 0 }} />
              )}
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: step > s.n ? '#8B1E2D' : step === s.n ? '#111' : '#E0D8CF', color: step >= s.n ? '#fff' : '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, zIndex: 1, position: 'relative' }}>
                {step > s.n ? '✓' : s.n}
              </div>
              <div style={{ fontSize: 10, color: step === s.n ? '#111' : '#aaa', marginTop: 4, fontWeight: step === s.n ? 600 : 400 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px', maxWidth: 560, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

        {/* ── Step 1 ── */}
        {step === 1 && (
          <div>
            <h2 style={h2}>Set up your screen</h2>
            <p style={subText}>Power on your screen device — a QR code and token will appear. Enter the details below.</p>

            {errors.general && <div style={errBox}>{errors.general}</div>}

            <div style={section}>
              <div style={sectionTitle}>Device token</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Shown at the bottom of the activation screen on your TV.</div>
              <input value={token} onChange={e => setToken(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" style={{ ...inp, fontFamily: 'monospace', fontSize: 12, borderColor: errors.token ? '#e00' : '#E0D8CF' }} />
              {errors.token && <div style={errTxt}>{errors.token}</div>}
            </div>

            <div style={section}>
              <div style={sectionTitle}>Screen name</div>
              <input value={screenName} onChange={e => setScreenName(e.target.value)} placeholder="e.g. Main Display, Counter Screen" style={{ ...inp, borderColor: errors.screenName ? '#e00' : '#E0D8CF' }} />
              {errors.screenName && <div style={errTxt}>{errors.screenName}</div>}
            </div>

            <div style={{ ...section, marginBottom: 24 }}>
              <div style={sectionTitle}>Location name</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>The name of the place where this screen is installed.</div>
              <input value={locName} onChange={e => setLocName(e.target.value)} placeholder="e.g. Cafe Novo, Main Branch" style={{ ...inp, borderColor: errors.locName ? '#e00' : '#E0D8CF' }} />
              {errors.locName && <div style={errTxt}>{errors.locName}</div>}
            </div>

            <button onClick={handleStep1} disabled={busy} style={primaryBtn}>
              {busy ? 'Setting up...' : 'Continue →'}
            </button>
          </div>
        )}

        {/* ── Step 2 ── */}
        {step === 2 && (
          <div>
            <h2 style={h2}>Add content</h2>
            <p style={subText}>Add the content you want to display on your screen. You can add multiple items.</p>

            {errors.general && <div style={errBox}>{errors.general}</div>}

            {/* Added contents */}
            {contents.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={sectionTitle}>Added content ({contents.length})</div>
                {contents.map((c, i) => (
                  <div key={c.id} style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{c.type === 'image' ? '🖼️' : c.type === 'video' ? '🎬' : c.type === 'pdf' ? '📄' : c.type === 'url' ? '🌐' : '📋'}</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                    <span style={{ fontSize: 11, color: '#999' }}>{c.duration_sec}s</span>
                    <button onClick={() => handleRemoveContent(c.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: 16 }}>×</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add content form */}
            <div style={section}>
              <div style={sectionTitle}>Add new content</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {['image', 'video', 'pdf', 'url', 'data'].map(t => (
                  <button key={t} onClick={() => { setContentTab(t); setErrors({}); }}
                    style={{ padding: '5px 10px', borderRadius: 20, border: '1px solid #ddd', cursor: 'pointer', fontSize: 11, background: contentTab === t ? '#111' : '#fff', color: contentTab === t ? '#fff' : '#555' }}>
                    {typeLabel[t]}
                  </button>
                ))}
              </div>

              <input value={contentName} onChange={e => setContentName(e.target.value)} placeholder="Content name *" style={{ ...inp, marginBottom: 10, borderColor: errors.contentName ? '#e00' : '#E0D8CF' }} />
              {errors.contentName && <div style={errTxt}>{errors.contentName}</div>}

              {(contentTab === 'image' || contentTab === 'video' || contentTab === 'pdf') && (
                <div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginBottom: 10 }}>
                    📁 {contentFile ? contentFile.name : `Choose ${contentTab === 'pdf' ? 'PDF' : 'file'}`}
                    <input type="file" accept={acceptMap[contentTab]} onChange={e => setContentFile(e.target.files[0])} style={{ display: 'none' }} />
                  </label>
                  {errors.contentFile && <div style={errTxt}>{errors.contentFile}</div>}
                </div>
              )}
              {contentTab === 'url' && (
                <div>
                  <input value={contentUrl} onChange={e => setContentUrl(e.target.value)} placeholder="https://..." style={{ ...inp, marginBottom: 10, borderColor: errors.contentUrl ? '#e00' : '#E0D8CF' }} />
                  {errors.contentUrl && <div style={errTxt}>{errors.contentUrl}</div>}
                </div>
              )}
              {contentTab === 'data' && (
                <div>
                  <textarea value={contentBody} onChange={e => setContentBody(e.target.value)} placeholder="Content text" style={{ ...inp, height: 80, resize: 'vertical', marginBottom: 10, borderColor: errors.contentBody ? '#e00' : '#E0D8CF' }} />
                  {errors.contentBody && <div style={errTxt}>{errors.contentBody}</div>}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: '#555' }}>Duration:</span>
                <input type="number" value={contentDuration} onChange={e => setContentDuration(parseInt(e.target.value) || 10)} style={{ ...inp, width: 70 }} />
                <span style={{ fontSize: 13, color: '#555' }}>seconds</span>
              </div>

              <button onClick={handleAddContent} disabled={uploading} style={{ ...secondaryBtn, width: '100%' }}>
                {uploading ? 'Uploading...' : '+ Add to list'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={() => setStep(1)} style={backBtn}>← Back</button>
              <button onClick={() => { if (contents.length === 0) { setErrors({ general: 'Add at least one content item before continuing.' }); return; } setErrors({}); setStep(3); }} style={{ ...primaryBtn, flex: 1 }}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3 ── */}
        {step === 3 && (
          <div>
            <h2 style={h2}>Create a playlist</h2>
            <p style={subText}>A playlist groups your content items and defines the order they play on screen.</p>

            {errors.general && <div style={errBox}>{errors.general}</div>}

            <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1rem', marginBottom: 16 }}>
              <div style={sectionTitle}>Content to be added ({contents.length} items)</div>
              {contents.map((c, i) => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F0EBE6', fontSize: 13 }}>
                  <span style={{ color: '#999', fontSize: 11 }}>{i + 1}</span>
                  <span>{c.type === 'image' ? '🖼️' : c.type === 'video' ? '🎬' : c.type === 'pdf' ? '📄' : c.type === 'url' ? '🌐' : '📋'}</span>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 11, color: '#999' }}>{c.duration_sec}s</span>
                </div>
              ))}
            </div>

            <div style={{ ...section, marginBottom: 24 }}>
              <div style={sectionTitle}>Playlist name</div>
              <input value={playlistName} onChange={e => setPlaylistName(e.target.value)} placeholder="e.g. Main Menu, Promotions" style={{ ...inp, borderColor: errors.playlistName ? '#e00' : '#E0D8CF' }} />
              {errors.playlistName && <div style={errTxt}>{errors.playlistName}</div>}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(2)} style={backBtn}>← Back</button>
              <button onClick={handleCreatePlaylist} disabled={busy} style={{ ...primaryBtn, flex: 1 }}>
                {busy ? 'Creating...' : 'Create & Activate →'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Done ── */}
        {step === 4 && (
          <div style={{ textAlign: 'center', paddingTop: 32 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <h2 style={{ ...h2, textAlign: 'center' }}>You're all set!</h2>
            <p style={{ ...subText, textAlign: 'center', marginBottom: 24 }}>
              Your screen is now active and playing your content. You can manage everything from the dashboard.
            </p>

            <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1rem', marginBottom: 24, textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 }}>
                <span style={{ color: '#777' }}>Location</span><span style={{ fontWeight: 500 }}>{locName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 }}>
                <span style={{ color: '#777' }}>Screen</span><span style={{ fontWeight: 500 }}>{screenName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 }}>
                <span style={{ color: '#777' }}>Playlist</span><span style={{ fontWeight: 500 }}>{playlistName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}>
                <span style={{ color: '#777' }}>Content items</span><span style={{ fontWeight: 500 }}>{contents.length}</span>
              </div>
            </div>

            <button onClick={onComplete} style={{ ...primaryBtn, width: '100%' }}>
              Go to Dashboard →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const h2 = { fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: '#111' };
const subText = { fontSize: 14, color: '#777', margin: '0 0 20px', lineHeight: 1.6 };
const section = { background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1rem', marginBottom: 12 };
const sectionTitle = { fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 10 };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #E0D8CF', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const primaryBtn = { background: '#8B1E2D', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const secondaryBtn = { background: '#fff', color: '#555', border: '1px solid #E0D8CF', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer' };
const backBtn = { background: 'none', color: '#555', border: '1px solid #E0D8CF', borderRadius: 8, padding: '12px 20px', fontSize: 14, cursor: 'pointer' };
const errBox = { background: '#FDE8E8', color: '#9B2335', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 };
const errTxt = { fontSize: 11, color: '#c00', marginTop: 4 };